import { format, getDay } from 'date-fns'

export type DurationUnit = 'milliseconds' | 'seconds' | 'minutes' | 'hours'

// Duration split into totals (`as*` fields) and clock components
// (`hours` 0-23, `minutes`/`seconds` 0-59), all read as plain fields.
export type DurationParts = {
  asMilliseconds: number
  asSeconds: number
  asMinutes: number
  asHours: number
  hours: number
  minutes: number
  seconds: number
}

const MS_PER_UNIT: Record<DurationUnit, number> = {
  milliseconds: 1,
  seconds: 1000,
  minutes: 60000,
  hours: 3600000
}

export function getDurationFromStartAndEndTime(
  // Callers derive startTime from an optional entity (e.g. an inactive drill),
  // so it is genuinely absent at times; the arithmetic below yields NaN parts,
  // which is the honest result for an indeterminate span.
  startTime: number | undefined,
  endTime?: number | null,
  unit: DurationUnit = 'seconds'
): DurationParts {
  // When endTime is missing, measure against the current time (epoch seconds).
  // Clamp negatives to zero: a negative span is meaningless and would otherwise
  // render malformed clock strings ('-1:-1:-1') downstream.
  const value = Math.max(
    0,
    (endTime || Math.floor(Date.now() / 1000)) - (startTime ?? NaN)
  )
  const asMilliseconds = value * MS_PER_UNIT[unit]
  const totalSeconds = Math.floor(asMilliseconds / 1000)

  return {
    asMilliseconds,
    asSeconds: asMilliseconds / 1000,
    asMinutes: asMilliseconds / 60000,
    asHours: asMilliseconds / 3600000,
    hours: Math.floor(asMilliseconds / 3600000) % 24,
    minutes: Math.floor(totalSeconds / 60) % 60,
    seconds: totalSeconds % 60
  }
}

export type FormattedDateItem = {
  value: string
  formatted: string
  formattedShort: string
}

export type FormattedDate = {
  unixMil: number
  unixSeconds: number
  time: FormattedDateItem
  weekday: FormattedDateItem
  day: FormattedDateItem
  month: FormattedDateItem
  year: FormattedDateItem
  full: FormattedDateItem
}

// All-'Invalid date' fallback, keyed off whatever unixMil/unixSeconds the
// caller already computed (NaN in every current case).
function invalidDateResult(unixMil: number, unixSeconds: number) {
  const item = () => ({
    value: 'Invalid date',
    formatted: 'Invalid date',
    formattedShort: 'Invalid date'
  })
  return {
    unixMil,
    unixSeconds,
    time: item(),
    weekday: item(),
    day: item(),
    month: item(),
    year: item(),
    full: item()
  }
}

export function timestampToFomattedDate(
  timeString: string | null | undefined
): FormattedDate {
  // A nullish or empty timestamp is absent by design (e.g. a blank session's
  // unset startTime/endTime) rather than corrupt data, so skip the error log.
  if (!timeString) {
    return invalidDateResult(NaN, NaN)
  }

  const unixMil = Date.parse(timeString)
  const unixSeconds = unixMil / 1000

  // date-fns format() throws on an invalid Date; return an all-'Invalid date'
  // result instead so callers keep a non-throwing contract.
  if (isNaN(unixMil)) {
    console.error(
      `timestampToFomattedDate received an unparseable date: ${JSON.stringify(timeString)}`
    )
    return invalidDateResult(unixMil, unixSeconds)
  }

  const date = new Date(unixMil)

  const base = {
    unixMil,
    unixSeconds,
    time: {
      value: format(date, 'h:mm aaa'),
      formattedShort: format(date, 'HH:mm'),
      formatted: format(date, 'HH:mm:ss')
    },
    weekday: {
      value: String(getDay(date)),
      formattedShort: format(date, 'EEE'),
      formatted: format(date, 'EEEE')
    },
    day: {
      value: format(date, 'd'),
      formattedShort: format(date, 'dd'),
      formatted: format(date, 'do')
    },
    month: {
      value: format(date, 'M'),
      formattedShort: format(date, 'MMM'),
      formatted: format(date, 'MMMM')
    },
    year: {
      value: format(date, 'yyyy'),
      formattedShort: format(date, 'yy'),
      formatted: format(date, 'yyyy')
    }
  }

  const full = {
    value: `${base.day.formattedShort}/${base.month.value}/${base.year.formattedShort}`,
    formatted: `${base.weekday.formatted}, ${base.day.formatted} ${base.month.formatted} ${base.year.formatted}`,
    formattedShort: `${base.weekday.formattedShort}, ${base.day.formatted} ${base.month.formattedShort} ${base.year.formattedShort}`
  }

  return { ...base, full }
}

// Format an absolute timestamp (ms, ISO string, or Date) as a local wall-clock time.
export function formatTime(
  value: number | string | Date,
  pattern = 'HH:mm:ss'
) {
  const date = new Date(value)

  // date-fns format() throws on an invalid Date; degrade to 'Invalid date'.
  if (isNaN(date.getTime())) {
    console.error(
      `formatTime received an unparseable value: ${JSON.stringify(value)}`
    )
    return 'Invalid date'
  }

  return format(date, pattern)
}

// Trimmed duration clock for sub-day spans: `M:SS` under an hour (minutes
// unpadded), `H:MM:SS` at or above an hour. Callers only pass sub-day values
// (drill/session lengths); durations past 24h are out of range.
export function formatDurationClock(ms: number) {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor(totalSeconds / 60) % 60
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}

// Zero-padded elapsed clock `HH:mm:ss`. Distinct from formatDurationClock:
// every unit is padded, including a leading zero hour.
export function formatElapsedClock(ms: number) {
  // Clamp negatives: an event timed before its session start would otherwise
  // render '-1:-1:-1' instead of a zeroed clock.
  const totalSeconds = Math.floor(Math.max(0, ms) / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor(totalSeconds / 60) % 60
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export function getSecondsFromDateString(dateString: string): number {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string')
  }

  return Math.floor(date.getTime() / 1000)
}
